/**
 * Face-edge kernel — MOVE / COPY / ROTATE of sketch geometry (2026-09-27, Matt: "I
 * should be able to select any sketched edge(s), face(s), or combination of these and
 * execute a move, move a copy, or rotate command. The edges, faces, and solids should
 * behave like SketchUp in that they have a stickiness to each other which causes the
 * shape to morph").
 *
 * SketchUp's model, which this follows: geometry is a graph of shared vertices, so
 * moving a sub-entity moves ITS VERTICES and everything attached to them stretches —
 * a moved face drags the faces around it along; an edge moved sideways tilts its
 * neighbors. Two things SketchUp does about "impossible" results, and what we do:
 *
 * - a face that would bend out of flat: SketchUp either constrains the move or
 *   auto-folds the face with a new diagonal edge (its Alt/Cmd auto-fold). v1 here
 *   REFUSES with a message (auto-fold is the recorded K4 item) — never a warped face.
 * - geometry pushed through itself: SketchUp lets faces pass through each other and
 *   leaves them unintersected until you run Intersect Faces. We are stricter, by the
 *   same rule as push/pull: the result must pass the validity gate (invariants + no
 *   edge piercing a face), otherwise the move is refused and nothing changes.
 *
 * After the move the geometry SETTLES exactly like a push/pull landing: corners that
 * land on corners weld, corners landing mid-edge split that edge, spikes and flattened
 * faces drop out, coplanar seams through the touched corners dissolve.
 */

import type { BodyTopology, BodyPt3, BodyVid, BodyEid, BodyFid } from './types'
import { WELD_TOL, planeDist, planeFromPoints, pointSegDist, samePlane } from './geom3'
import { facesUsingEdge, loopPoints, gcOrphanVertices, invariants, splitEdge } from './topo'
import { insertSegment } from './arrange'
import { edgePiercesFace, dissolveEdge, weldVertices, removeSpikes } from './extrude'

const PLANE_TOL = WELD_TOL * 2
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T

export type TransformResult = { ok: true } | { ok: false; error: string }

function loopTails(topo: BodyTopology, loop: { edges: BodyEid[]; dirs: boolean[] }): BodyVid[] {
  return loop.edges.map((eid, i) => {
    const e = topo.edges[eid]
    return loop.dirs[i] ? e.a : e.b
  })
}

/** vertices of a set of edges + faces (a sketch sub-selection) */
export function verticesOf(topo: BodyTopology, edges: BodyEid[], faces: BodyFid[]): Set<BodyVid> {
  const out = new Set<BodyVid>()
  for (const eid of edges) { const e = topo.edges[eid]; if (e) { out.add(e.a); out.add(e.b) } }
  for (const fid of faces) for (const loop of topo.faces[fid]?.loops || []) for (const v of loopTails(topo, loop)) out.add(v)
  return out
}

/** edges of a set of edges + faces (their loop edges included) */
export function edgesOf(topo: BodyTopology, edges: BodyEid[], faces: BodyFid[]): Set<BodyEid> {
  const out = new Set<BodyEid>(edges.filter((e) => topo.edges[e]))
  for (const fid of faces) for (const loop of topo.faces[fid]?.loops || []) for (const e of loop.edges) out.add(e)
  return out
}

/**
 * Move the vertices in `vids` (null = every vertex, a rigid whole-body move) through
 * `map`. Works on a scratch copy; `topo` is only replaced when the result is sound.
 */
export function transformVertices(
  topo: BodyTopology, vids: Set<BodyVid> | null, map: (p: BodyPt3) => BodyPt3,
): TransformResult {
  const work = clone(topo)
  const moved = new Set<BodyVid>(vids ?? Object.keys(work.vertices))
  if (!moved.size) return { ok: false, error: 'Nothing to move.' }
  for (const v of moved) {
    const p = work.vertices[v]
    if (!p) continue
    const q = map(p)
    if (!Number.isFinite(q.x) || !Number.isFinite(q.y) || !Number.isFinite(q.z)) return { ok: false, error: 'Invalid move.' }
    work.vertices[v] = q
  }
  // every face touching a moved vertex must still be FLAT (re-fit its plane); a face
  // only PARTLY moved must not flip over (its normal reversing = the shape turning
  // inside out — a box top dragged down past its own bottom)
  for (const f of Object.values(work.faces)) {
    const tails = f.loops.flatMap((l) => loopTails(work, l))
    const touches = tails.some((v) => moved.has(v))
    if (!touches) continue
    const partly = tails.some((v) => !moved.has(v))
    const nBefore = f.plane.n
    const outer = loopPoints(work, f.loops[0])
    const pl = planeFromPoints(outer)
    if (!pl) return { ok: false, error: 'That would collapse a face.' }
    for (const l of f.loops) for (const q of loopPoints(work, l)) {
      if (Math.abs(planeDist(pl, q)) > PLANE_TOL) {
        return { ok: false, error: 'That would bend a face out of flat — move the whole face, or along it.' }
      }
    }
    if (partly && nBefore.x * pl.n.x + nBefore.y * pl.n.y + nBefore.z * pl.n.z < 0) {
      return { ok: false, error: 'That would turn the shape inside out — not moved.' }
    }
    f.plane = pl
  }
  const settled = settleAfterMove(work, moved)
  if (!settled.ok) return settled
  if (invariants(work).length || edgePiercesFace(work)) {
    return { ok: false, error: 'That would pass through the shape — not moved.' }
  }
  topo.vertices = work.vertices
  topo.edges = work.edges
  topo.faces = work.faces
  topo.curves = work.curves
  return { ok: true }
}

/**
 * Copy a set of edges (a sub-selection's edges, face loops included) through `map`
 * INTO the same body: each copied edge goes through insertSegment, so a copy that
 * lands on existing geometry sticks to it (splits it, subdivides faces) and a copied
 * face's closed loop heals back into a face. Validity-gated like a move.
 */
export type CopyResult = { ok: true; edges: BodyEid[]; faces: BodyFid[] } | { ok: false; error: string }

/** the edges of `topo` lying along any of `segs` (both ends on one segment), and
 *  the faces whose every loop edge is one of them — what a copy "made", even when it
 *  landed on existing geometry and only split it (SketchUp selects the copy) */
function edgesAlong(topo: BodyTopology, segs: [BodyPt3, BodyPt3][]): { edges: BodyEid[]; faces: BodyFid[] } {
  const tol = WELD_TOL * 4
  const on = (p: BodyPt3) => segs.findIndex(([a, b]) => pointSegDist(p, a, b).d <= tol)
  const edges: BodyEid[] = []
  for (const [eid, e] of Object.entries(topo.edges)) {
    const p = topo.vertices[e.a], q = topo.vertices[e.b]
    if (!p || !q) continue
    const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2, z: (p.z + q.z) / 2 }
    const i = on(mid)
    if (i < 0) continue
    const [a, b] = segs[i]
    if (pointSegDist(p, a, b).d <= tol && pointSegDist(q, a, b).d <= tol) edges.push(eid)
  }
  const set = new Set(edges)
  const faces = Object.entries(topo.faces)
    .filter(([, f]) => f.loops.every((l) => l.edges.every((e) => set.has(e))))
    .map(([fid]) => fid)
  return { edges, faces }
}

export function copyEdges(topo: BodyTopology, eids: Set<BodyEid>, map: (p: BodyPt3) => BodyPt3): CopyResult {
  const work = clone(topo)
  const segs: [BodyPt3, BodyPt3][] = []
  for (const eid of eids) {
    const e = work.edges[eid]
    if (!e) continue
    segs.push([map(work.vertices[e.a]), map(work.vertices[e.b])])
  }
  if (!segs.length) return { ok: false, error: 'Nothing to copy.' }
  for (const [a, b] of segs) insertSegment(work, a, b)
  if (invariants(work).length || edgePiercesFace(work)) {
    return { ok: false, error: 'That copy would pass through the shape — not copied.' }
  }
  topo.vertices = work.vertices
  topo.edges = work.edges
  topo.faces = work.faces
  topo.curves = work.curves
  return { ok: true, ...edgesAlong(topo, segs) }
}

/** weld/split/clean after moved vertices may now TOUCH the rest of the body */
function settleAfterMove(topo: BodyTopology, moved: Set<BodyVid>): TransformResult {
  const loneBefore = new Set(Object.keys(topo.edges).filter((eid) => !facesUsingEdge(topo, eid).length))
  const weld = new Map<BodyVid, BodyVid>()
  const touched = new Set<BodyVid>(moved)
  for (const v of moved) {
    const p = topo.vertices[v]
    if (!p) continue
    for (const [w, q] of Object.entries(topo.vertices)) {
      if (moved.has(w)) continue
      const dx = p.x - q.x, dy = p.y - q.y, dz = p.z - q.z
      if (dx * dx + dy * dy + dz * dz <= WELD_TOL * WELD_TOL) { weld.set(v, w); touched.add(w); break }
    }
  }
  // moved corners mid-way along an unmoved edge split it (and vice versa)
  for (const v of moved) {
    if (weld.has(v)) continue
    const p = topo.vertices[v]
    for (const [eid, e] of Object.entries(topo.edges)) {
      if (moved.has(e.a) || moved.has(e.b)) continue
      const { d, t } = pointSegDist(p, topo.vertices[e.a], topo.vertices[e.b])
      if (d <= WELD_TOL && t > 1e-9 && t < 1 - 1e-9) { splitEdge(topo, eid, v); break }
    }
  }
  for (const [w, q] of Object.entries(topo.vertices)) {
    if (moved.has(w) || touched.has(w)) continue
    for (const [eid, e] of Object.entries(topo.edges)) {
      if (!(moved.has(e.a) || moved.has(e.b)) || e.a === w || e.b === w) continue
      const { d, t } = pointSegDist(q, topo.vertices[e.a], topo.vertices[e.b])
      if (d <= WELD_TOL && t > 1e-9 && t < 1 - 1e-9) { splitEdge(topo, eid, w); touched.add(w); break }
    }
  }
  if (weld.size) weldVertices(topo, weld)
  removeSpikes(topo)
  for (const [fid, f] of Object.entries(topo.faces)) {
    if (f.loops.some((l) => l.edges.length < 3)) delete topo.faces[fid]
  }
  for (const eid of Object.keys(topo.edges)) {
    if (!loneBefore.has(eid) && !facesUsingEdge(topo, eid).length && (touched.has(topo.edges[eid].a) || touched.has(topo.edges[eid].b))) {
      delete topo.edges[eid]
    }
  }
  for (const eid of Object.keys(topo.edges)) {
    const e = topo.edges[eid]
    if (!e) continue
    const a = weld.get(e.a) ?? e.a, b = weld.get(e.b) ?? e.b
    if (!(touched.has(a) || touched.has(b))) continue
    const users = facesUsingEdge(topo, eid)
    if (users.length !== 2 || !samePlane(topo.faces[users[0]].plane, topo.faces[users[1]].plane)) continue
    dissolveEdge(topo, eid)
  }
  gcOrphanVertices(topo)
  if (!Object.keys(topo.edges).length) return { ok: false, error: 'That would remove the whole shape.' }
  return { ok: true }
}
